import type { InfiniteData, QueryFunctionContext } from '@tanstack/react-query';
import { infiniteQueryOptions, useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { apiClient } from 'lib/api';
import queryString from 'query-string';
import type { GetRedditPostsParams, RedditPost, RedditPostsListResponse, RedditTone } from '../types';
import { RedditTones } from '../types';
import queryKeys from './queryKeys';

type RedditPostsRequestParams = Omit<GetRedditPostsParams, 'offset'>;

const toStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    return [value];
  }

  return [];
};

const toTone = (value: unknown): RedditTone | null => {
  if (typeof value !== 'string') {
    return null;
  }

  return RedditTones.find((tone) => tone.toLowerCase() === value.trim().toLowerCase()) ?? null;
};

const normalizePost = (post: RedditPost): RedditPost => ({
  ...post,
  pain_point: toStringArray(post.pain_point),
  tone: toTone(post.tone),
  hook_ideas: toStringArray(post.hook_ideas),
  creative_scripts: toStringArray(post.creative_scripts),
  key_phrases: toStringArray(post.key_phrases),
});

export const getRedditPosts = async ({
  signal,
  queryKey,
  pageParam,
}: QueryFunctionContext<ReturnType<ReturnType<typeof queryKeys>['posts']>, number>): Promise<RedditPostsListResponse> => {
  const [, , params] = queryKey;
  const res = await apiClient.get<RedditPostsListResponse>(`/reddit/posts`, {
    params: { ...params, offset: pageParam },
    signal,
    paramsSerializer: (prms) => queryString.stringify(prms, { arrayFormat: 'comma' }),
  });
  return res.data;
};

export interface RedditPostsInfiniteData {
  pages: RedditPost[][];
  count: number;
  loadTs?: string;
}

export const getRedditPostsOptions = (params: RedditPostsRequestParams) =>
  infiniteQueryOptions<
    RedditPostsListResponse,
    Error,
    RedditPostsInfiniteData,
    ReturnType<ReturnType<typeof queryKeys>['posts']>,
    number
  >({
    staleTime: 60 * 30 * 1000,
    queryFn: getRedditPosts,
    queryKey: queryKeys().posts(params),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (!lastPage.data.length) {
        return null;
      }
      const nextOffset = lastPageParam + lastPage.meta.limit;
      return nextOffset < lastPage.meta.count ? nextOffset : null;
    },
    select: (data: InfiniteData<RedditPostsListResponse>) => ({
      count: data.pages[0]?.meta.count ?? 0,
      loadTs: data.pages[0]?.meta.load_ts,
      pages: data.pages.map((page) => page.data.map(normalizePost)),
    }),
  });

export const useRedditPosts = (params: RedditPostsRequestParams) =>
  useSuspenseInfiniteQuery(getRedditPostsOptions(params));
